// Quirks: seeded minor traits for captains and ships, shown as lists on the
// loadout cards. Mostly flavor; ~17% rare stat nudge; ~3% epic. Deterministic
// per seed — the re-roll button fishes for a cool set as much as a new face.

import type { CaptainEffectMods } from '../types.js';

export interface Quirk {
  text: string;
  rarity: 'common' | 'rare' | 'epic';
  mods?: Partial<CaptainEffectMods>;
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface PoolEntry {
  text: string;
  mods?: Partial<CaptainEffectMods>;
}

const CAPTAIN_FLAVOR: string[] = [
  'Left-handed', 'Chews matchsticks', 'Hums off-key', 'Keeps a lucky die',
  'Never blinks first', 'Writes letters home', 'Collects starmaps', 'Afraid of fish',
  'Sings to the reactor', 'Always cold', 'Reads the manual', 'Pockets sugar packets',
];

const CAPTAIN_RARE: PoolEntry[] = [
  { text: 'Steady hands', mods: { damageMult: 0.03 } },
  { text: 'Quick on the stick', mods: { moveSpeedMult: 0.03 } },
  { text: 'Hair-trigger', mods: { fireRateMult: 0.03 } },
  { text: 'Magnetic personality', mods: { magnetRadiusMult: 0.04 } },
  { text: 'Thick skin', mods: { maxHpDelta: 4 } },
  { text: 'Meditates daily', mods: { shieldRegenMultBonus: 0.05 } },
];

const CAPTAIN_EPIC: PoolEntry[] = [
  { text: 'Born for the void', mods: { damageMult: 0.06, fireRateMult: 0.03 } },
  { text: 'Reactor whisperer', mods: { shieldRegenMultBonus: 0.1 } },
  { text: 'Ace of the fleet', mods: { moveSpeedMult: 0.06, fireRateMult: 0.02 } },
];

const SHIP_FLAVOR: string[] = [
  'Rusty but proud', 'Smells of ozone', 'Named twice', 'Patchwork hull',
  'Ex-military surplus', 'Still under warranty', 'Rattles above 80%', 'Coffee-stained console',
  'Squeaky landing gear', 'One crooked panel', 'Smuggler refit', 'Blessed twice',
];

const SHIP_RARE: PoolEntry[] = [
  { text: 'Tuned injectors', mods: { fireRateMult: 0.03 } },
  { text: 'Light frame', mods: { moveSpeedMult: 0.03 } },
  { text: 'Dense plating', mods: { maxHpDelta: 5 } },
  { text: 'Tractor coil', mods: { magnetRadiusMult: 0.04 } },
  { text: 'Overclocked guns', mods: { damageMult: 0.03 } },
  { text: 'Spare shield cell', mods: { shieldRegenMultBonus: 0.05 } },
];

const SHIP_EPIC: PoolEntry[] = [
  { text: 'Prototype core', mods: { damageMult: 0.05, fireRateMult: 0.05 } },
  { text: 'Ghost hull', mods: { moveSpeedMult: 0.08 } },
  { text: 'War veteran', mods: { maxHpDelta: 10 } },
];

function rollQuirks(seed: number, flavor: string[], rare: PoolEntry[], epic: PoolEntry[], count: number): Quirk[] {
  const rnd = mulberry32(seed);
  const out: Quirk[] = [];
  const used = new Set<number>();
  let guard = 0;
  while (out.length < count && guard++ < 32) {
    const r = rnd();
    if (r < 0.03) {
      const idx = Math.floor(rnd() * epic.length);
      if (used.has(1000 + idx)) continue;
      used.add(1000 + idx);
      out.push({ text: epic[idx]!.text, rarity: 'epic', mods: epic[idx]!.mods });
    } else if (r < 0.2) {
      const idx = Math.floor(rnd() * rare.length);
      if (used.has(500 + idx)) continue;
      used.add(500 + idx);
      out.push({ text: rare[idx]!.text, rarity: 'rare', mods: rare[idx]!.mods });
    } else {
      const idx = Math.floor(rnd() * flavor.length);
      if (used.has(idx)) continue;
      used.add(idx);
      out.push({ text: flavor[idx]!, rarity: 'common' });
    }
  }
  return out;
}

/** Two quirks for a captain seed (flavor-driven, occasionally mechanical). */
export function rollCaptainQuirks(seed: number): Quirk[] {
  return rollQuirks((seed ^ 0xC4A1B2C3) >>> 0, CAPTAIN_FLAVOR, CAPTAIN_RARE, CAPTAIN_EPIC, 2);
}

/** Two quirks for a ship seed. */
export function rollShipQuirks(seed: number): Quirk[] {
  return rollQuirks((seed ^ 0x51B1E5E7) >>> 0, SHIP_FLAVOR, SHIP_RARE, SHIP_EPIC, 2);
}

/** Sum quirk mods into an existing mods object (captain/effects.ts shape). */
export function applyQuirkMods(mods: CaptainEffectMods, quirks: readonly Quirk[]): CaptainEffectMods {
  for (const q of quirks) {
    if (!q.mods) continue;
    for (const [key, value] of Object.entries(q.mods)) {
      const k = key as keyof CaptainEffectMods;
      mods[k] += value ?? 0;
    }
  }
  return mods;
}
